import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { LatexRenderer } from '../../components/ui/LatexRenderer';
export type FractionVisualType = 'pizza' | 'bar' | 'circle-clean';
interface FractionVisualProps {
  numerator: number;
  denominator: number;
  label?: string;
  type?: FractionVisualType;
  showEaten?: boolean;
  /** 'stimulus' = compact visual only (shown in-question), 'support' = educational panel (shown after answer) */
  mode?: 'stimulus' | 'support';
  /** For showing multiple fractions (e.g., addition: 1/4 + 2/4) */
  fractions?: Array<{
    numerator: number;
    denominator: number;
    label?: string;
    visualType?: FractionVisualType;
  }>;
  /** Operator to show between fractions (default: '+') */
  operator?: '+' | '-' | '×' | '÷';
  /** Optional equivalent fraction label (e.g. "1/2 = 2/4") */
  equivalentLabel?: string;
  /** Optional divisor lines to show partition concept (e.g. how many times 1/8 fits in 3/4) */
  divisorLines?: number;
}
function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}
// ─── Pizza SVG ────────────────────────────────────────────────────────────────
function PizzaSVG({
  filledSlices,
  totalSlices,
  size = 120,
  animationDelay = 0
}: {
  filledSlices: number;
  totalSlices: number;
  size?: number;
  animationDelay?: number;
}) {
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 6;
  const crustR = r + 4;
  const slices = Array.from({
    length: totalSlices
  }, (_, i) => {
    const startDeg = i * 360 / totalSlices - 90;
    const endDeg = (i + 1) * 360 / totalSlices - 90;
    const startRad = startDeg * Math.PI / 180;
    const endRad = endDeg * Math.PI / 180;
    const largeArc = 360 / totalSlices > 180 ? 1 : 0;
    const x1 = cx + r * Math.cos(startRad);
    const y1 = cy + r * Math.sin(startRad);
    const x2 = cx + r * Math.cos(endRad);
    const y2 = cy + r * Math.sin(endRad);
    const isFilled = i < filledSlices;
    return {
      x1,
      y1,
      x2,
      y2,
      startRad,
      endRad,
      largeArc,
      isFilled
    };
  });
  return <motion.svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`Taartdiagram: ${filledSlices} van ${totalSlices} stukken gekleurd`} initial={{
    opacity: 0,
    scale: 0.88
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    delay: animationDelay,
    type: 'spring',
    stiffness: 240,
    damping: 18
  }} className="drop-shadow-md" data-id="element-12925" data-mp="1k1fbelcfbc4v">
      {/* Crust ring */}
      <circle cx={cx} cy={cy} r={crustR} fill="#92400E" data-id="element-12926" data-mp="s8533arrtt9i" />
      {/* Base disc */}
      <circle cx={cx} cy={cy} r={r} fill="#FDE68A" data-id="element-12927" data-mp="sa9gqkfh26mz" />

      {/* Slices */}
      {totalSlices === 1 ?
    // Special case: whole pizza
    <circle cx={cx} cy={cy} r={r} fill="#F59E0B" data-id="element-12928" data-mp="2mlehngz6c4ym" /> : slices.map((s, i) => <motion.path key={i} d={`M ${cx} ${cy} L ${s.x1} ${s.y1} A ${r} ${r} 0 ${s.largeArc} 1 ${s.x2} ${s.y2} Z`} fill={s.isFilled ? '#F59E0B' : '#E5E7EB'} stroke="white" strokeWidth={size > 100 ? 2.5 : 2} strokeLinejoin="round" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: animationDelay + i * 0.04
    }} data-id="element-12929" data-mp="2wrfsg12vr70t" data-mpi={i} />)}

      {/* Divider lines from center (drawn on top for crispness) */}
      {totalSlices > 1 && slices.map((s, i) => <line key={`line-${i}`} x1={cx} y1={cy} x2={s.x1} y2={s.y1} stroke="white" strokeWidth={size > 100 ? 2.5 : 2} strokeLinecap="round" data-id="element-12930" data-mp="3kffi0xoggrdt" data-mpi={i} />)}
    </motion.svg>;
}
// ─── Circle Clean SVG ─────────────────────────────────────────────────────────
function CircleCleanSVG({
  filledSlices,
  totalSlices,
  size = 120,
  animationDelay = 0
}: {
  filledSlices: number;
  totalSlices: number;
  size?: number;
  animationDelay?: number;
}) {
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 4;
  const slices = Array.from({
    length: totalSlices
  }, (_, i) => {
    const startDeg = i * 360 / totalSlices - 90;
    const endDeg = (i + 1) * 360 / totalSlices - 90;
    const startRad = startDeg * Math.PI / 180;
    const endRad = endDeg * Math.PI / 180;
    const largeArc = 360 / totalSlices > 180 ? 1 : 0;
    const x1 = cx + r * Math.cos(startRad);
    const y1 = cy + r * Math.sin(startRad);
    const x2 = cx + r * Math.cos(endRad);
    const y2 = cy + r * Math.sin(endRad);
    const isFilled = i < filledSlices;
    return {
      x1,
      y1,
      x2,
      y2,
      startRad,
      endRad,
      largeArc,
      isFilled
    };
  });
  return <motion.svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" initial={{
    opacity: 0,
    scale: 0.88
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    delay: animationDelay,
    type: 'spring',
    stiffness: 240,
    damping: 18
  }} className="drop-shadow-sm" data-id="element-12931" data-mp="mjoh0lntpii3">
      <circle cx={cx} cy={cy} r={r} fill="#e0e7ff" stroke="#c7d2fe" strokeWidth="2" data-id="element-12932" data-mp="3mnagwemfac1" />
      {totalSlices === 1 ? <circle cx={cx} cy={cy} r={r} fill="#6366f1" data-id="element-12933" data-mp="3q1oqk5911vxt" /> : slices.map((s, i) => <motion.path key={i} d={`M ${cx} ${cy} L ${s.x1} ${s.y1} A ${r} ${r} 0 ${s.largeArc} 1 ${s.x2} ${s.y2} Z`} fill={s.isFilled ? '#6366f1' : 'transparent'} stroke="#c7d2fe" strokeWidth="1.5" strokeLinejoin="round" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: animationDelay + i * 0.04
    }} data-id="element-12934" data-mp="3l351vx31u61r" data-mpi={i} />)}
    </motion.svg>;
}
// ─── Bar visual ───────────────────────────────────────────────────────────────
function BarSVG({
  filledSlices,
  totalSlices,
  animationDelay = 0,
  divisorLines
}: {
  filledSlices: number;
  totalSlices: number;
  animationDelay?: number;
  divisorLines?: number;
}) {
  const width = 200;
  const height = 40;
  const sliceW = width / totalSlices;
  return <motion.svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" style={{
    maxWidth: width,
    display: 'block'
  }} role="img" aria-label={`Staafdiagram: ${filledSlices} van ${totalSlices} delen gekleurd`} initial={{
    opacity: 0,
    y: 4
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: animationDelay,
    duration: 0.3
  }} className="drop-shadow-sm" data-id="element-12935" data-mp="3bdy5h504vdvg">
      <rect x={0} y={0} width={width} height={height} rx={6} fill="#E5E7EB" data-id="element-12936" data-mp="2676o435sih3g" />
      {Array.from({
      length: totalSlices
    }, (_, i) => {
      const isFilled = i < filledSlices;
      const rx = i === 0 ? 6 : 0;
      const rrx = i === totalSlices - 1 ? 6 : 0;
      return isFilled ? <motion.rect key={i} x={i * sliceW} y={0} width={sliceW} height={height} fill="#F59E0B" rx={rx} initial={{
        scaleX: 0,
        originX: 0
      }} animate={{
        scaleX: 1
      }} transition={{
        delay: animationDelay + i * 0.05,
        duration: 0.25
      }} data-id="element-12937" data-mp="19iihe2ziyg9c" /> : null;
    })}
      {/* Dividers */}
      {Array.from({
      length: totalSlices - 1
    }, (_, i) => <line key={`div-${i}`} x1={(i + 1) * sliceW} y1={0} x2={(i + 1) * sliceW} y2={height} stroke="white" strokeWidth={2} data-id="element-12938" data-mp="3my93zym94m0u" />)}
      {/* Secondary Divisor Lines */}
      {divisorLines && divisorLines > 1 && Array.from({
      length: divisorLines - 1
    }, (_, i) => {
      const x = (i + 1) * width / divisorLines;
      return <line key={`div-sec-${i}`} x1={x} y1={0} x2={x} y2={height} stroke="#94A3B8" strokeWidth={1.5} strokeDasharray="4 2" data-id="element-12939" data-mp="eiyw29vokml5" />;
    })}
      <rect x={0} y={0} width={width} height={height} rx={6} fill="none" stroke="#D1D5DB" strokeWidth={1.5} data-id="element-12940" data-mp="28sxegpjz4xyv" />
    </motion.svg>;
}
// ─── Main component ───────────────────────────────────────────────────────────
export function FractionVisual({
  numerator,
  denominator,
  label,
  type = 'pizza',
  showEaten = false,
  mode = 'support',
  fractions,
  operator = '+',
  equivalentLabel,
  divisorLines
}: FractionVisualProps) {
  // ── MULTI-FRACTION MODE (for addition/subtraction/multiplication) ─────────
  if (fractions && fractions.length > 0) {
    return <div className="flex flex-col items-center gap-5 py-6 px-4 sm:px-8 bg-slate-50/80 border border-slate-200 rounded-2xl shadow-sm w-full max-w-2xl mx-auto" data-id="element-12941" data-mp="2dqpds4my7md3">
        {label && <p className="text-base font-semibold text-slate-700 text-center mb-2" data-id="element-12942" data-mp="1780nwyokgfr9">
            <LatexRenderer content={label} data-id="element-12943" data-mp="12dd6tkvw6nmp" />
          </p>}

        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6 w-full" data-id="element-12944" data-mp="2cxde5o2ahsym">
          {fractions.map((frac, idx) => {
          const safeDen = isFiniteNumber(frac.denominator) ? Math.max(1, Math.floor(frac.denominator)) : 1;
          const safeNum = isFiniteNumber(frac.numerator) ? Math.max(0, Math.floor(frac.numerator)) : 0;
          const visualType = frac.visualType || type;
          return <Fragment key={idx} data-id="element-12945" data-mp="2qmxdejwnw836" data-mpi={idx}>
                {idx > 0 && <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-indigo-100 border-2 border-white shadow-sm shrink-0 z-10 -my-2 sm:my-0 sm:-mx-3" data-id="element-12946" data-mp="34nhth8b6zpsz">
                    <span className="text-2xl sm:text-3xl font-black text-indigo-600 leading-none pb-0.5" data-id="element-12947" data-mp="1a9qled8ihdzn">
                      {operator}
                    </span>
                  </div>}
                <div className="flex flex-col items-center gap-3 bg-white p-4 rounded-xl border border-slate-100 shadow-sm w-full sm:w-auto min-w-[220px]" data-id="element-12948" data-mp="w4go99c3hgbx">
                  {visualType === 'bar' ? <BarSVG filledSlices={safeNum} totalSlices={safeDen} animationDelay={idx * 0.1} divisorLines={divisorLines} data-id="element-12949" data-mp="mex29ut356cu" /> : visualType === 'circle-clean' ? <CircleCleanSVG filledSlices={safeNum} totalSlices={safeDen} size={mode === 'stimulus' ? 100 : 120} animationDelay={idx * 0.1} data-id="element-12950" data-mp="hf2dbwsvequf" /> : <PizzaSVG filledSlices={safeNum} totalSlices={safeDen} size={mode === 'stimulus' ? 100 : 120} animationDelay={idx * 0.1} data-id="element-12951" data-mp="8ggus1nefbtn" />}
                  {frac.label && <span className="text-sm font-semibold text-slate-600" data-id="element-12952" data-mp="1wcepsc3oia0l">
                      <LatexRenderer content={frac.label} data-id="element-12953" data-mp="1wehnlib2p7fv" />
                    </span>}
                </div>
              </Fragment>;
        })}
        </div>
      </div>;
  }
  // ── SINGLE FRACTION MODE (original behavior) ──────────────────────────────
  const safeDen = isFiniteNumber(denominator) ? Math.max(1, Math.floor(denominator)) : 1;
  const safeNum = isFiniteNumber(numerator) ? Math.max(0, Math.floor(numerator)) : 0;
  const wholeItems = Math.floor(safeNum / safeDen);
  const remainder = safeNum % safeDen;
  // How many visuals to render
  const visuals: Array<{
    filled: number;
    total: number;
  }> = [...Array.from({
    length: wholeItems
  }, () => ({
    filled: safeDen,
    total: safeDen
  })), ...(remainder > 0 ? [{
    filled: remainder,
    total: safeDen
  }] : []), ...(safeNum === 0 ? [{
    filled: 0,
    total: safeDen
  }] : [])];
  // ── STIMULUS MODE: clean visual only, no explanations ────────────────────
  if (mode === 'stimulus') {
    return <div className="flex flex-col items-center gap-3 py-2" data-id="element-12954" data-mp="3k7t9vpsq7k30">
        {label && <p className="text-xs font-semibold text-slate-500 text-center" data-id="element-12955" data-mp="3o9okjkwjr9tg">
            <LatexRenderer content={label} data-id="element-12956" data-mp="3t9j9hiwrhpbv" />
          </p>}
        <div className="flex flex-wrap justify-center items-center gap-3" data-id="element-12957" data-mp="1vn18hii02tb">
          {visuals.map((v, i) => type === 'bar' ? <BarSVG key={i} filledSlices={v.filled} totalSlices={v.total} animationDelay={i * 0.08} divisorLines={divisorLines} data-id="element-12958" data-mp="6uhs86qmcxoi" /> : type === 'circle-clean' ? <CircleCleanSVG key={i} filledSlices={v.filled} totalSlices={v.total} size={100} animationDelay={i * 0.08} data-id="element-12959" data-mp="1djsm7xptfyz4" /> : <PizzaSVG key={i} filledSlices={v.filled} totalSlices={v.total} size={100} animationDelay={i * 0.08} data-id="element-12960" data-mp="1mccnl5xhshnn" />)}
        </div>
      </div>;
  }
  // ── SUPPORT MODE: visual + clean fraction label ───────────────────────────
  return <div className="flex flex-col items-center gap-4 py-3" data-id="element-12961" data-mp="13mwlie65sh54">
      {label && <p className="text-sm font-semibold text-slate-600 text-center" data-id="element-12962" data-mp="yntkd4tauv2f">
          <LatexRenderer content={label} data-id="element-12963" data-mp="ypwhs9ws7qh6" />
        </p>}

      {/* Visuals */}
      <div className="flex flex-wrap justify-center items-center gap-4" data-id="element-12964" data-mp="2ezyv2p9uc6fs">
        {visuals.map((v, i) => type === 'bar' ? <BarSVG key={i} filledSlices={v.filled} totalSlices={v.total} animationDelay={i * 0.1} divisorLines={divisorLines} data-id="element-12965" data-mp="2ijz7l4s0fr52" /> : type === 'circle-clean' ? <CircleCleanSVG key={i} filledSlices={v.filled} totalSlices={v.total} size={140} animationDelay={i * 0.1} data-id="element-12966" data-mp="28ne2kjfzqg18" /> : <PizzaSVG key={i} filledSlices={v.filled} totalSlices={v.total} size={140} animationDelay={i * 0.1} data-id="element-12967" data-mp="31cl0bg7mf1lf" />)}
      </div>

      {/* Fraction label */}
      <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 shadow-sm" data-id="element-12968" data-mp="2wigvb2hx13mz">
        {/* Fraction notation */}
        <div className="flex flex-col items-center leading-none" data-id="element-12969" data-mp="3k79ur8xqxtmc">
          <span className="text-2xl font-black text-amber-700" data-id="element-12970" data-mp="3p1dyzkfmnnjq">{safeNum}</span>
          <div className="w-8 h-0.5 bg-amber-400 my-1.5 rounded-full" data-id="element-12971" data-mp="2pclpak617mc" />
          <span className="text-2xl font-black text-slate-700" data-id="element-12972" data-mp="3tw6yu6s5a2js">{safeDen}</span>
        </div>

        {equivalentLabel && <>
            <span className="text-xl font-bold text-slate-400 mx-1" data-id="element-12973" data-mp="3ow124c70ppuu">=</span>
            <div className="flex items-center" data-id="element-12974" data-mp="3kwscnjj0ge53">
              <span className="text-lg font-bold text-slate-700" data-id="element-12975" data-mp="3ky3g1i8jnjvo">
                <LatexRenderer content={equivalentLabel} data-id="element-12976" data-mp="3fxpemj1g786m" />
              </span>
            </div>
          </>}

        <div className="w-px h-10 bg-amber-200 ml-2" data-id="element-12977" data-mp="3b4wbu0pji9x6" />
        {/* Description */}
        <p className="text-sm font-medium text-slate-700 leading-snug max-w-[160px]" data-id="element-12978" data-mp="367w1hdlnql09">
          {wholeItems > 0 && remainder > 0 ? <>
              <span className="text-emerald-600 font-bold" data-id="element-12979" data-mp="2x66eql2q0a8t">{wholeItems}</span>{' '}
              heel
              {wholeItems > 1 ? 'e' : ''} +{' '}
              <span className="text-amber-600 font-bold" data-id="element-12980" data-mp="2s7v2k3oydnnf">
                {remainder}/{safeDen}
              </span>
            </> : wholeItems > 0 ? <>
              <span className="text-emerald-600 font-bold" data-id="element-12981" data-mp="1cnce1cvhlxtm">{wholeItems}</span>{' '}
              hele pizza{wholeItems > 1 ? "'s" : ''}
            </> : <>
              <span className="text-amber-600 font-bold" data-id="element-12982" data-mp="1lpbuo42lazw1">{safeNum}</span> van de{' '}
              <span className="font-bold" data-id="element-12983" data-mp="oz2vqkxhfs78">{safeDen}</span> stukken
            </>}
        </p>
      </div>
    </div>;
}